/* ============================================================================
   AKINOD · Page FAQ
   ----------------------------------------------------------------------------
   Ce que la section `akinod-faq-page` ne sait pas faire sans script :

     · la recherche dans la page : à partir de 2 caractères, insensible à la
       casse et aux accents, sur la question ET la réponse (tableau et extrait
       des CGV compris). Les questions trouvées s'ouvrent, le terme est surligné
       dans la question, les rubriques et intertitres vides disparaissent, les
       cartes et les onglets aussi, et le compteur dit ce qu'il a trouvé ;
     · l'onglet de la rubrique en cours de lecture, et sa pastille gardée à
       l'écran quand la barre déborde (mobile) ;
     · le défilement doux vers une rubrique ;
     · l'ouverture directe d'une question par son adresse, #q-<ancre>, que la
       maquette annonçait sans l'avoir faite ;
     · dans le formulaire de contact, la liste des questions de la rubrique
       choisie, le numéro de commande exigé par certaines questions, et les
       liens des réponses vers la page Contact, qui descendent au formulaire
       avec leur question déjà choisie.

   Sans ce fichier, la page reste entière : les questions s'ouvrent (ce sont des
   <details>), les cartes et les onglets sont des ancres, le champ envoie vers
   la recherche du site, et le formulaire part avec son seul sujet.

   Idempotent (marquage `data-ak-faq`) : l'éditeur de thème réinjecte les
   sections sans recharger la page.
   ========================================================================== */

(() => {
  'use strict';

  const REDUCED = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  const MIN = 2;

  /* --- Comparaison ---------------------------------------------------------
     Minuscules, sans accents. L'espace insécable (posé devant « ? ») et
     l'apostrophe courbe valent leurs équivalents tapés au clavier.

     La normalisation se fait CARACTÈRE PAR CARACTÈRE, avec la position
     d'origine de chacun : c'est ce qui permet de surligner le bon morceau du
     texte affiché. La maquette normalisait la chaîne entière et reportait les
     positions telles quelles, ce qui décale le surlignage dès qu'un caractère
     change de longueur en minuscules (« İ » en donne deux). */

  function plier(c) {
    return c
      .toLowerCase()
      .normalize('NFD')
      .replace(/[̀-ͯ]/g, '')
      .replace(/ | /g, ' ')
      .replace(/[‘’]/g, "'");
  }

  const norm = (s) => Array.from(s || '', plier).join('');

  /** Le texte normalisé et, pour chacun de ses caractères, sa position d'origine. */
  function indexer(s) {
    let texte = '';
    const pos = [];
    let i = 0;
    for (const c of s) {
      const p = plier(c);
      texte += p;
      for (let k = 0; k < p.length; k += 1) pos.push(i);
      i += c.length;
    }
    pos.push(i);
    return { texte, pos };
  }

  /* --- Surlignage ------------------------------------------------------------ */

  function demarquer(el) {
    const marks = el.querySelectorAll('mark.ak-faqp__mark');
    if (!marks.length) return;
    marks.forEach((m) => m.replaceWith(document.createTextNode(m.textContent)));
    el.normalize();
  }

  function marquer(el, terme) {
    const walker = document.createTreeWalker(el, NodeFilter.SHOW_TEXT);
    const noeuds = [];
    for (let n = walker.nextNode(); n; n = walker.nextNode()) noeuds.push(n);

    noeuds.forEach((t) => {
      const brut = t.nodeValue;
      const { texte, pos } = indexer(brut);
      let i = texte.indexOf(terme);
      if (i < 0) return;

      const frag = document.createDocumentFragment();
      let depuis = 0;
      while (i >= 0) {
        const debut = pos[i];
        const fin = pos[i + terme.length];
        frag.appendChild(document.createTextNode(brut.slice(depuis, debut)));
        const m = document.createElement('mark');
        m.className = 'ak-faqp__mark';
        m.textContent = brut.slice(debut, fin);
        frag.appendChild(m);
        depuis = fin;
        i = texte.indexOf(terme, i + terme.length);
      }
      frag.appendChild(document.createTextNode(brut.slice(depuis)));
      t.parentNode.replaceChild(frag, t);
    });
  }

  /** Le texte cherchable d'une question : sans son numéro ni le libellé
      « Extrait de nos conditions générales de vente », qui répondrait sinon à
      « conditions » dans toutes les questions qui citent les CGV. */
  function texteCherchable(qa) {
    const copie = qa.cloneNode(true);
    copie.querySelectorAll('[data-ak-faqp-ignore]').forEach((el) => el.remove());
    return norm(copie.textContent.replace(/\s+/g, ' '));
  }

  /* --- Défilement --------------------------------------------------------------
     Au-dessus de 990 px, c'est `.page-wrapper` qui défile et non la fenêtre (voir
     akinod-behaviors.js). `scrollIntoView` fait défiler le bon conteneur quel
     qu'il soit, et respecte le `scroll-margin-top` qui garde le titre sous les
     deux barres collantes. Horizon pose déjà `scroll-behavior: smooth` sur les
     deux : un saut d'ancre natif serait doux lui aussi. Si on intercepte quand
     même le clic, c'est pour montrer la rubrique avant de la viser (voir
     `devoiler`) et lui donner le focus. */

  function allerA(cible) {
    devoiler(cible);
    cible.scrollIntoView({ behavior: REDUCED ? 'auto' : 'smooth', block: 'start' });
  }

  /** Une rubrique pas encore apparue est décalée de 26 px (`.ak-reveal`), et
      `scrollIntoView` vise la boîte décalée : mesuré, la rubrique s'arrêtait
      26 px trop haut, sous la barre d'onglets. On la montre donc d'un coup,
      sans transition, avant de viser. Elle arrive de toute façon à l'écran. */
  function devoiler(el) {
    const bloc = el.closest('.ak-reveal');
    if (!bloc || bloc.classList.contains('is-visible')) return;
    bloc.style.transition = 'none';
    bloc.classList.add('is-visible');
    void bloc.offsetHeight;   // l'état final s'applique avant la mesure
    requestAnimationFrame(() => { bloc.style.transition = ''; });
  }

  /* --- Amorçage d'une section -------------------------------------------------- */

  function initFaq(root) {
    if (!root || root.dataset.akFaq) return;
    root.dataset.akFaq = '1';

    const form = root.querySelector('[data-ak-faqp-form]');
    const input = root.querySelector('[data-ak-faqp-input]');
    const effacer = root.querySelector('[data-ak-faqp-clear]');
    const compteur = root.querySelector('[data-ak-faqp-count]');
    const vide = root.querySelector('[data-ak-faqp-empty]');
    const caches = Array.from(root.querySelectorAll('[data-ak-faqp-cache]'));
    const sections = Array.from(root.querySelectorAll('[data-ak-faqp-sec]'));
    const questions = Array.from(root.querySelectorAll('[data-ak-faqp-qa]'));
    const onglets = Array.from(root.querySelectorAll('[data-ak-faqp-tab]'));
    const rangee = root.querySelector('[data-ak-faqp-tabrow]');

    const textes = new Map(questions.map((qa) => [qa, texteCherchable(qa)]));
    const compteurInitial = compteur ? compteur.textContent : '';

    /* Une question ouverte par la recherche porte `data-ak-faqp-auto`. Dès que
       le visiteur y touche lui-même, elle redevient la sienne : la recherche ne
       la refermera plus. Le clic couvre aussi Entrée et Espace sur <summary>. */
    questions.forEach((qa) => {
      qa.querySelector('summary')?.addEventListener('click', () => {
        delete qa.dataset.akFaqpAuto;
      });
    });

    /* --- Recherche ------------------------------------------------------------ */

    function libelle(cle, n, terme) {
      const modele = root.dataset[cle] || '';
      return modele.replace(/\[n\]/g, String(n)).replace(/\[terme\]/g, terme);
    }

    function chercher() {
      if (!input) return;
      const brut = input.value.trim().replace(/\s+/g, ' ');
      const terme = norm(brut);
      const actif = terme.length >= MIN;
      form?.classList.toggle('is-filled', input.value.length > 0);

      let trouves = 0;

      sections.forEach((sec) => {
        let visibles = 0;

        sec.querySelectorAll('[data-ak-faqp-qa]').forEach((qa) => {
          const titre = qa.querySelector('[data-ak-faqp-qt]');
          if (titre) demarquer(titre);

          const touche = !actif || textes.get(qa).includes(terme);
          qa.hidden = !touche;
          if (touche) visibles += 1;

          if (actif && touche) {
            trouves += 1;
            if (titre) marquer(titre, terme);
            if (!qa.open) {
              qa.dataset.akFaqpAuto = '1';
              qa.open = true;
            }
          } else if (qa.dataset.akFaqpAuto) {
            // Fin de recherche, ou question écartée par un terme plus précis :
            // on referme ce que la recherche avait ouvert, et seulement ça.
            delete qa.dataset.akFaqpAuto;
            qa.open = false;
          }
        });

        // Un intertitre sans question visible sous lui disparaît aussi.
        sec.querySelectorAll('[data-ak-faqp-sub]').forEach((sub) => {
          let el = sub.nextElementSibling;
          let reste = false;
          while (el && !el.hasAttribute('data-ak-faqp-sub')) {
            if (el.hasAttribute('data-ak-faqp-qa') && !el.hidden) { reste = true; break; }
            el = el.nextElementSibling;
          }
          sub.hidden = !reste;
        });

        sec.hidden = actif && visibles === 0;
      });

      caches.forEach((el) => { el.hidden = actif; });
      if (vide) vide.hidden = !(actif && trouves === 0);

      if (compteur) {
        if (!actif) compteur.textContent = compteurInitial;
        else if (trouves === 0) compteur.textContent = libelle('resultNone', 0, brut);
        else compteur.textContent = libelle(trouves > 1 ? 'resultOther' : 'resultOne', trouves, brut);
      }
    }

    if (input) {
      input.addEventListener('input', chercher);
      // Une valeur restaurée par le navigateur (retour arrière) est rejouée.
      if (input.value) chercher();
    }

    form?.addEventListener('submit', (e) => {
      e.preventDefault();
      chercher();
      // Sur mobile, fermer le clavier laisse voir les réponses trouvées.
      input?.blur();
    });

    effacer?.addEventListener('click', () => {
      input.value = '';
      chercher();
      input.focus();
    });

    /* --- Onglet actif -----------------------------------------------------------
       Une rubrique est « en cours » quand elle traverse la bande située entre
       40 % et 45 % de la hauteur de l'écran : les réglages de la maquette. Aucun
       onglet n'est allumé tant qu'on n'a pas atteint la première rubrique. */

    function allumer(id) {
      let actif = null;
      onglets.forEach((t) => {
        const on = t.dataset.akFaqpTab === id;
        t.classList.toggle('is-active', on);
        if (on) { t.setAttribute('aria-current', 'true'); actif = t; } else t.removeAttribute('aria-current');
      });

      // La rangée déborde (mobile) : on la fait glisser pour centrer la pastille
      // active. `scrollTo` sur la rangée, pas `scrollIntoView`, qui ferait aussi
      // bouger la page.
      if (!actif || !rangee || rangee.scrollWidth <= rangee.clientWidth + 1) return;
      const decalage = actif.getBoundingClientRect().left - rangee.getBoundingClientRect().left;
      const cible = rangee.scrollLeft + decalage - (rangee.clientWidth - actif.offsetWidth) / 2;
      rangee.scrollTo({ left: Math.max(0, cible), behavior: REDUCED ? 'auto' : 'smooth' });
    }

    if (onglets.length && 'IntersectionObserver' in window) {
      const io = new IntersectionObserver((entries) => {
        entries.forEach((en) => { if (en.isIntersecting) allumer(en.target.id); });
      }, { rootMargin: '-40% 0px -55% 0px' });
      sections.forEach((s) => { if (s.id) io.observe(s); });
    }

    /* --- Cartes et onglets : défilement doux -------------------------------------
       L'adresse prend l'ancre (on peut la copier), et le focus passe à la
       rubrique, comme après un saut d'ancre natif : la tabulation reprend là. */

    root.querySelectorAll('[data-ak-faqp-goto]').forEach((lien) => {
      lien.addEventListener('click', (e) => {
        const id = decodeURIComponent((lien.getAttribute('href') || '').slice(1));
        const cible = id && document.getElementById(id);
        if (!cible || !root.contains(cible)) return;
        e.preventDefault();
        allerA(cible);
        cible.focus({ preventScroll: true });
        if (history.pushState) history.pushState(null, '', `#${id}`);
      });
    });

    /* --- Arrivée par l'adresse : #q-<ancre> et #sec-<ancre> ------------------------
       Une question visée s'ouvre. Dans les deux cas on recale la cible : le saut
       natif a eu lieu sur une rubrique encore décalée par son apparition, et la
       page a pu bouger depuis (polices). */

    function ouvrirDepuisAncre() {
      let id = '';
      try { id = decodeURIComponent(window.location.hash.slice(1)); } catch { return; }
      if (!id.startsWith('q-') && !id.startsWith('sec-')) return;
      const cible = document.getElementById(id);
      if (!cible || !root.contains(cible)) return;
      if (cible.tagName === 'DETAILS') cible.open = true;
      devoiler(cible);
      requestAnimationFrame(() => allerA(cible));
    }

    ouvrirDepuisAncre();
    window.addEventListener('hashchange', ouvrirDepuisAncre);

    initFormulaire(root);
  }

  /* --- Formulaire de contact ------------------------------------------------------
     Liquid rend TOUTES les questions, groupées par rubrique, dans une liste
     masquée et désactivée : sans script, le formulaire part avec son seul sujet.
     Ici, on garde un instantané de chaque groupe, et la liste ne montre que
     celui de la rubrique choisie. Pour un sujet hors FAQ (revendeur, presse…),
     elle disparaît et reste désactivée : un champ désactivé ne part pas dans le
     courriel. Sur une rubrique, elle devient obligatoire ; « Autre question »
     y laisse une sortie.

     Une question peut exiger un numéro de commande (case de son bloc dans
     l'éditeur) : le champ, ou les commandes à cocher d'un client connecté,
     deviennent obligatoires et perdent leur mention « facultatif ». Chez un
     client connecté, « Une autre commande » ouvre le champ de son numéro, qui
     reste fermé (et désactivé, donc absent du courriel) sinon. */

  /* --- La requête client -----------------------------------------------------------
     Le statut 44 « Différée : Requête client » du site historique (04/10) : une
     question d'une rubrique, envoyée par un client connecté avec une de ses
     commandes cochée, retient la commande avant l'atelier. C'est la passerelle qui
     la retient, par le proxy de l'app Akinod Bundle (`/apps/sav`) : Shopify lui
     garantit le client connecté, ce que le formulaire ne pourrait pas.

     L'appel part **une fois le courriel parti**, pas au clic : à l'envoi on note la
     commande pour cet onglet, et c'est la page de confirmation qui prévient la
     passerelle. Un envoi que Shopify refuse — anti-spam abandonné, e-mail invalide —
     ne retient donc aucune commande sans que le service client le sache. Les sujets
     hors rubrique (revendeur, distributeur, presse, idée) ne retiennent rien, comme
     au site historique. */

  const CLE_REQUETE = 'ak-sav-requete';
  const UNE_HEURE = 3600000;

  function noterRequete(commandeId) {
    try {
      if (commandeId) sessionStorage.setItem(CLE_REQUETE, JSON.stringify({ commande: commandeId, le: Date.now() }));
      else sessionStorage.removeItem(CLE_REQUETE);
    } catch { /* stockage fermé (navigation privée) : la commande ne sera pas retenue */ }
  }

  function envoyerRequete() {
    let note = null;
    try {
      note = JSON.parse(sessionStorage.getItem(CLE_REQUETE) || 'null');
      sessionStorage.removeItem(CLE_REQUETE);
    } catch { return; }
    // Une note de plus d'une heure n'est pas celle de cet envoi.
    if (!note || !note.commande || Date.now() - note.le > UNE_HEURE) return;
    fetch('/apps/sav/requete-client', {
      method: 'POST',
      body: new URLSearchParams({ commande: String(note.commande) }),
      credentials: 'same-origin',
    }).catch(() => { /* le courriel est parti : le service client bloquera à la main */ });
  }

  function initFormulaire(root) {
    const boite = root.querySelector('[data-ak-sav]');
    if (boite?.querySelector('[data-ak-sav-envoye]')) envoyerRequete();
    const sujet = boite?.querySelector('[data-ak-sav-sujet]');
    const question = boite?.querySelector('[data-ak-sav-question]');
    const qbox = boite?.querySelector('[data-ak-sav-qbox]');
    // Après l'envoi, la boîte ne porte plus que le message de confirmation.
    if (!boite || !sujet || !question || !qbox) return;

    const commande = boite.querySelector('[data-ak-sav-commande]');
    const cases = Array.from(boite.querySelectorAll('[data-ak-sav-radio]'));
    const choixAutre = boite.querySelector('[data-ak-sav-choix-autre]');
    const autre = boite.querySelector('[data-ak-sav-autre]');
    const facultatif = boite.querySelector('[data-ak-sav-facultatif]');
    const message = boite.querySelector('textarea');

    const invite = question.options[0].cloneNode(true);
    const groupes = new Map();
    question.querySelectorAll('optgroup[data-ak-sav-groupe]').forEach((og) => {
      groupes.set(og.dataset.akSavGroupe, Array.from(og.children, (o) => o.cloneNode(true)));
    });

    function majCommande() {
      const choisie = question.disabled ? null : question.selectedOptions[0];
      const exige = Boolean(choisie && choisie.hasAttribute('data-ak-sav-exige'));
      if (commande) commande.required = exige;
      // Un groupe de boutons radio est satisfait dès qu'un seul est coché.
      cases.forEach((c) => { c.required = exige; });
      if (autre) {
        const ouvert = Boolean(choixAutre && choixAutre.checked);
        autre.hidden = !ouvert;
        autre.disabled = !ouvert;
        autre.required = ouvert;
      }
      if (facultatif) facultatif.hidden = exige;
    }

    cases.forEach((c) => c.addEventListener('change', () => {
      majCommande();
      if (c === choixAutre) autre?.focus();
    }));

    /** `garder` : reprend la question déjà choisie si elle est dans le groupe
        (valeurs restaurées par le navigateur au retour arrière). */
    function majQuestions(garder) {
      const avant = garder ? question.value : '';
      const groupe = groupes.get(sujet.selectedOptions[0]?.dataset.akSavRubrique || '');
      question.replaceChildren(invite.cloneNode(true), ...(groupe || []).map((o) => o.cloneNode(true)));
      qbox.hidden = !groupe;
      question.disabled = !groupe;
      question.required = Boolean(groupe);
      if (groupe && avant && groupe.some((o) => o.value === avant)) question.value = avant;
      majCommande();
    }

    sujet.addEventListener('change', () => majQuestions(false));
    question.addEventListener('change', majCommande);
    majQuestions(true);

    // La requête client : notée à l'envoi, transmise par la page de confirmation.
    boite.querySelector('form')?.addEventListener('submit', () => {
      const cochee = cases.find((c) => c.checked && c.dataset.akSavCommandeId);
      const rubrique = sujet.selectedOptions[0]?.dataset.akSavRubrique;
      noterRequete(cochee && rubrique ? cochee.dataset.akSavCommandeId : '');
    });

    /** Choisit la rubrique et la question d'ancre `ancre` (« commande-01 »…). */
    function choisir(ancre) {
      for (const [cle, options] of groupes) {
        const cible = options.find((o) => o.dataset.akSavQ === ancre);
        if (!cible) continue;
        const rubrique = Array.from(sujet.options).find((o) => o.dataset.akSavRubrique === cle);
        if (!rubrique) return;
        sujet.value = rubrique.value;
        majQuestions(false);
        question.value = cible.value;
        majCommande();
        return;
      }
    }

    /* Les réponses renvoient à la « plateforme SAV » par un lien vers la page
       Contact, écrit dans leur texte. Sur la FAQ, ce lien descend au formulaire
       avec la question de la réponse déjà choisie. L'adresse est comparée sans
       son préfixe de langue : en allemand, la page s'appelle /de/pages/kontakt,
       et une réponse traduite peut avoir gardé /pages/contact. */
    const sansLangue = (p) => p.replace(/^\/[a-z]{2}(?:-[a-z]{2})?(?=\/)/i, '').replace(/\/+$/, '');
    const pageContact = sansLangue(boite.dataset.akSavContact || '');

    function versFormulaire(lien) {
      let url;
      try { url = new URL(lien.getAttribute('href'), window.location.href); } catch { return false; }
      if (url.origin !== window.location.origin) return false;
      if (url.hash === '#formulaire') return true;
      const chemin = sansLangue(url.pathname);
      return chemin === '/pages/contact' || (pageContact !== '' && chemin === pageContact);
    }

    root.addEventListener('click', (e) => {
      if (e.defaultPrevented || e.button !== 0 || e.metaKey || e.ctrlKey || e.shiftKey || e.altKey) return;
      const lien = e.target.closest?.('.ak-faqp__a a[href]');
      if (!lien || !root.contains(lien) || !versFormulaire(lien)) return;
      e.preventDefault();
      const qa = lien.closest('[data-ak-faqp-qa]');
      if (qa && qa.id.startsWith('q-')) choisir(qa.id.slice(2));
      allerA(boite.querySelector('form') || boite);
      // Le premier champ utile : la commande quand elle est exigée, sinon le message.
      let suivant = message;
      if (commande && commande.required && !commande.value) suivant = commande;
      else if (cases.length && cases[0].required && !cases.some((c) => c.checked)) suivant = cases[0];
      suivant?.focus({ preventScroll: true });
    });
  }

  function initToutes(scope = document) {
    const racines = scope.matches?.('[data-ak-faqp]')
      ? [scope]
      : Array.from(scope.querySelectorAll('[data-ak-faqp]'));
    racines.forEach(initFaq);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => initToutes(document));
  } else {
    initToutes(document);
  }

  // Éditeur de thème : la section réinjectée est amorcée à neuf, et une
  // question choisie dans la barre latérale s'ouvre pour qu'on la voie.
  document.addEventListener('shopify:section:load', (e) => initToutes(e.target));
  document.addEventListener('shopify:block:select', (e) => {
    const el = e.target;
    if (el && el.matches?.('details[data-ak-faqp-qa]')) el.open = true;
  });
})();
